import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganizationId, useSubjectLanguageVariants } from '@features/user';
import { FeatureGates, useEventTracking, useFeatureGate } from '@services/statsig';

import { useSendRequirementRequestMutation } from '../store/api';
import { useOpenRequirements } from '../store/useOpenRequirements';

import { RequirementAlertBadge } from './RequirementAlertBadge';

export interface PatientRequirementAlertProps {
  patientId: string | undefined;
  /** Only used to attribute analytics to the visit; not required to render. */
  encounterId?: string;
}

/**
 * The recording-window provider alert (GDN-3225, AC-15).
 *
 * Renders nothing when the gate is off, while loading, or when the patient owes
 * nothing — the context bar is dense, and an alert that is sometimes an empty
 * placeholder trains providers to ignore it.
 */
export const PatientRequirementAlert = ({
  patientId,
  encounterId,
}: PatientRequirementAlertProps) => {
  const enabled = useFeatureGate(FeatureGates.PATIENT_REQUIREMENTS);
  const subject = useSubjectLanguageVariants();
  const organizationId = useOrganizationId();
  const navigate = useNavigate();
  const { patientRequirementsProviderAlert } = useEventTracking();

  const { items, hasUnnamedItems, isLoading } = useOpenRequirements(patientId, enabled);
  const [sendRequest, { isLoading: isReminding }] = useSendRequirementRequestMutation();
  const [remindOutcome, setRemindOutcome] = useState<'sent' | 'failed' | undefined>();

  const shown = enabled && !isLoading && items.length > 0;

  // One shown event per patient, so a provider scrolling the encounter does not
  // inflate the count. Keyed on the patient rather than the mount because the
  // bar survives patient changes within a session.
  // The context bar is reused across patients within a session, so state that
  // describes ONE patient has to be cleared when the patient changes —
  // otherwise the next patient's checklist opens showing the previous
  // patient's "Sent" confirmation mid-visit.
  // Tracks which patient the bar is currently showing, so a reply that arrives
  // after a switch can tell it is stale. A ref rather than state because the
  // async continuation below needs the value at settle time, not at call time.
  const currentPatientRef = useRef(patientId);
  // Identifies the in-flight send by REQUEST, not by patient. Keying on the
  // patient alone means two overlapping sends for the same patient share one
  // marker, so whichever finishes first clears it and re-enables Remind while
  // the other is still going. A counter gives each send its own identity.
  const remindSeq = useRef(0);
  const pendingRemind = useRef<{ id: number; patientId: string } | null>(null);

  useEffect(() => {
    currentPatientRef.current = patientId;
    setRemindOutcome(undefined);
  }, [patientId]);

  const shownFor = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!shown || !patientId || shownFor.current === patientId) return;
    shownFor.current = patientId;
    patientRequirementsProviderAlert.opened({
      organization_id: organizationId ?? undefined,
      patient_id: patientId,
      encounter_id: encounterId,
      open_count: items.length,
    });
  }, [
    shown,
    patientId,
    encounterId,
    items.length,
    organizationId,
    patientRequirementsProviderAlert,
  ]);

  if (!shown || !patientId) return null;

  const handleRemind = () => {
    patientRequirementsProviderAlert.remindClicked({
      organization_id: organizationId ?? undefined,
      patient_id: patientId,
      encounter_id: encounterId,
      open_count: items.length,
    });

    // Capture who this send is for. Clearing state on patient change is not
    // enough on its own: a slow reply for Patient A can settle AFTER the switch
    // and write A's outcome into B's freshly cleared state, telling the
    // provider that the patient in front of them has been reminded.
    const sentFor = patientId;
    const requestId = ++remindSeq.current;
    pendingRemind.current = { id: requestId, patientId: sentFor };

    void (async () => {
      try {
        const outcome = await sendRequest({
          orgId: organizationId ?? '',
          patientId,
          // The contract calls these "channels to attempt" and answers with
          // accepted vs failed per channel, so it resolves what actually goes
          // out. Both are requested because a provider mid-visit is the wrong
          // person to be picking a channel. NOTE: the contract does not state
          // that per-patient sends filter by the patient's channel
          // preferences — AC-12 guarantees that for bulk launch only — so this
          // deliberately does not claim it does.
          sendRequestBody: { channels: ['sms', 'email'] },
        }).unwrap();

        // A 200 means the request was processed, not that a message went out.
        // With every channel rejected there is nothing for the patient to act
        // on, so telling the provider "Sent" would be the same mistake as
        // treating delivery as completion.
        // Two conditions, not one: the bar must still be on the patient this
        // was sent for, AND this must be the newest send — an older reply
        // settling last would otherwise overwrite a newer one's result.
        if (currentPatientRef.current !== sentFor) return;
        if (remindSeq.current !== requestId) return;
        setRemindOutcome(outcome.accepted_channels.length > 0 ? 'sent' : 'failed');
      } catch {
        if (currentPatientRef.current !== sentFor) return;
        if (remindSeq.current !== requestId) return;
        setRemindOutcome('failed');
      } finally {
        // Only the newest send may clear the marker. An older one settling
        // late must leave a newer request's spinner alone.
        if (pendingRemind.current?.id === requestId) {
          pendingRemind.current = null;
        }
      }
    })();
  };

  return (
    <RequirementAlertBadge
      // Remount per patient: the badge owns its popover anchor, and an open
      // checklist must not survive the patient switching underneath it.
      key={patientId}
      items={items}
      hasUnnamedItems={hasUnnamedItems}
      subject={subject}
      onRemind={organizationId ? handleRemind : undefined}
      isReminding={isReminding && pendingRemind.current?.patientId === patientId}
      remindOutcome={remindOutcome}
      onOpenPatient={() => {
        patientRequirementsProviderAlert.openRecordClicked({
          organization_id: organizationId ?? undefined,
          patient_id: patientId,
          encounter_id: encounterId,
        });
        navigate(`/patient/${patientId}`);
      }}
      onOpen={() =>
        patientRequirementsProviderAlert.checklistOpened({
          organization_id: organizationId ?? undefined,
          patient_id: patientId,
          encounter_id: encounterId,
          open_count: items.length,
        })
      }
    />
  );
};
